<template>
	<Dialog
		:dialog-visible="visible"
		:persistent-dialog="true"
		agree-text="Get the App"
		cancel-text="Not Now"
		:options="{ width: '480px' }"
		@agree="onGetApp"
		@cancel="onNotNow">
		<template #dialog-header="{ titleId }">
			<div class="d-flex align-center ga-3 w-100">
				<div class="deprecation-icon d-flex align-center justify-center flex-shrink-0">
					<v-icon size="24" color="primary">icon-devices-stroke</v-icon>
				</div>
				<h5 :id="titleId" v-test="'ios-home-screen-deprecation-title'">Switch to the LiveSwitch app</h5>
			</div>
		</template>
		<template #dialog-body>
			<div v-test="'ios-home-screen-deprecation-body'" class="deprecation-body">
				<p class="mb-3">
					We're retiring the Home Screen version of LiveSwitch. The LiveSwitch app for iPhone and iPad is faster, more reliable,
					and built for recording on the go.
				</p>
				<p class="mb-4">Download it free and sign in with your existing account.</p>
				<div class="d-flex justify-center">
					<a
						v-test="'ios-home-screen-deprecation-app-store-badge'"
						v-track="'ios-home-screen-deprecation-app-store-badge'"
						class="app-store-badge-link"
						:href="MOBILE_APP_STORE_URL"
						target="_blank"
						rel="noopener noreferrer"
						@click="onBadgeClick">
						<img src="@/assets/app-store-badge.svg" alt="Download on the App Store" class="app-store-badge-image" />
					</a>
				</div>
			</div>
		</template>
	</Dialog>
</template>

<script setup lang="ts">
	import { datadogRum } from "@datadog/browser-rum";
	import { ref, watch } from "vue";

	import Dialog from "@/components/Dialog.vue";
	import { MOBILE_APP_STORE_URL } from "@/constants/mobileAppStores";
	import {
		decideIosHomeScreenDeprecationDialog,
		IOS_HOME_SCREEN_DEPRECATION_GET_APP_SNOOZE_DAYS,
		IOS_HOME_SCREEN_DEPRECATION_NOT_NOW_SNOOZE_DAYS,
		snoozeIosHomeScreenDeprecationDialog
	} from "@/helpers/iosHomeScreenDeprecation";
	import type { MeWithConfig } from "@/helpers/meFeatureFlags";

	/**
	 * LSP-22421 (parent LSP-22420): after login in the iOS Home Screen web app, tell the user that way of
	 * using the dashboard is being retired and send them to the App Store. Phase 1 of two — this only
	 * warns; Phase 2 (LSP-22422) blocks capture.
	 *
	 * App.vue mounts this once and passes `me`; the gate (kill switch on /me, Home Screen launch, iOS,
	 * device-local snooze) lives in helpers/iosHomeScreenDeprecation.ts and re-runs whenever `me` changes
	 * (first load, org switch, refresh). `suppressed` lets App.vue hold it back while another boot
	 * dialog (the welcome wizard) is up, so two persistent dialogs never stack.
	 *
	 * Copy is a first draft for product to iterate on — the wording lives only in this template.
	 */
	const props = withDefaults(
		defineProps<{
			me: MeWithConfig | null | undefined;
			suppressed?: boolean;
		}>(),
		{ suppressed: false }
	);

	const visible = ref(false);
	let launchContextForTelemetry: Record<string, unknown> = {};

	function evaluate() {
		if (visible.value) return;
		if (props.suppressed) {
			console.info("[ios-home-screen-deprecation] dialog suppressed", { reason: "another-boot-dialog-open" });
			return;
		}
		const decision = decideIosHomeScreenDeprecationDialog({ me: props.me });
		if (!decision.show) return;
		launchContextForTelemetry = { ...decision.launchContext };
		visible.value = true;
		track("shown");
	}

	function track(event: "shown" | "get_app" | "get_app_blocked" | "not_now") {
		try {
			datadogRum.addAction(`ios_home_screen_deprecation.${event}`, { launchContext: launchContextForTelemetry });
		} catch (error) {
			console.warn(`[ios-home-screen-deprecation] could not record "${event}"`, error);
		}
	}

	function close(snoozeDays: number) {
		snoozeIosHomeScreenDeprecationDialog(snoozeDays);
		visible.value = false;
	}

	/** The store badge is a real link so iOS hands it to the App Store itself; we only record and snooze. */
	function onBadgeClick() {
		track("get_app");
		close(IOS_HOME_SCREEN_DEPRECATION_GET_APP_SNOOZE_DAYS);
	}

	function onGetApp() {
		// A Home Screen web app has no tabs: window.open hands the URL to the system, which routes
		// apps.apple.com to the App Store. No feature string: "noopener" makes window.open return null
		// even when it succeeds, which is indistinguishable from a blocked open (Bugbot on PR 6671).
		// The opener is cut by hand instead.
		const opened = window.open(MOBILE_APP_STORE_URL, "_blank");
		if (!opened) {
			// Never navigate THIS window to the store: unloading the dashboard would cut off any upload
			// AsyncBackgroundUploader is running. Leave the dialog open, unsnoozed, so the App Store
			// badge link in the body is still one tap away.
			console.warn("[ios-home-screen-deprecation] window.open returned null; leaving the dialog open on the App Store badge");
			// Its own action, so a blocked open is never counted as a get_app conversion (AI review on PR 6671).
			track("get_app_blocked");
			return;
		}
		track("get_app");
		try {
			opened.opener = null;
		} catch (error) {
			console.warn("[ios-home-screen-deprecation] could not clear the App Store window's opener", error);
		}
		close(IOS_HOME_SCREEN_DEPRECATION_GET_APP_SNOOZE_DAYS);
	}

	function onNotNow() {
		track("not_now");
		close(IOS_HOME_SCREEN_DEPRECATION_NOT_NOW_SNOOZE_DAYS);
	}

	watch(() => [props.me, props.suppressed], evaluate, { immediate: true });
</script>

<style scoped lang="scss">
	.deprecation-icon {
		width: 40px;
		height: 40px;
		border-radius: 10px;
		background: rgba(var(--v-theme-primary), 0.12);
	}

	.app-store-badge-link {
		display: inline-flex;
		line-height: 0;
	}

	.app-store-badge-image {
		height: 40px;
		width: auto;
	}
</style>
